import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Select from "react-select";
import apiUrl from "../utility/apiUrl";
import Cookies from "js-cookie";
import { indianStates, courses, selectStyles, enquiryInputClass as inputClass } from "../utility/enquiryFormData";

const EnquiryPopupForm = ({ onClose }) => {
  // Starts visible: AnimatePresence animates the initial mount via the
  // motion.div's `initial`/`animate` props below, so no RAF-delayed flip is
  // needed just to trigger an entrance transition.
  const [visible, setVisible] = useState(true);
  const [formData, setFormData] = useState({
    name: Cookies.get("username") || "",
    email: Cookies.get("email") || "",
    phone: Cookies.get("phoneNo") || "",
    state: null,
    course: null,
    consent: false,
  });

  const [status, setStatus] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const autoCloseTimer = useRef(null);

  useEffect(() => {
    return () => {
      if (autoCloseTimer.current) clearTimeout(autoCloseTimer.current);
    };
  }, []);

  const handleStateChange = (selectedOption) => {
    setFormData({ ...formData, state: selectedOption });
  };

  const handleCourseChange = (selectedOption) => {
    setFormData({ ...formData, course: selectedOption });
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Just flips state — AnimatePresence's onExitComplete (below) calls the
  // parent's onClose once the exit transition actually finishes, instead of
  // guessing at a fixed setTimeout delay.
  const handleClose = () => setVisible(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    const token = Cookies.get("token");
    const accountType = Cookies.get("accountType");

    const isCollege = token && accountType === "college";
    const isAdmin = token && accountType === "admin";
    const isAuthor = token && accountType === "author";
    if (isCollege || isAdmin || isAuthor) {
      setStatus({ type: "error", message: "Access denied." });
      return;
    }

    if (!formData.state || !formData.course) {
      setStatus({ type: "error", message: "Please select state and course." });
      return;
    }

    const headers = {
      "Content-Type": "application/json",
    };
    if (token && accountType === "student") {
      headers.Authorization = token;
    }

    const requestData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      state: formData.state.value,
      course: formData.course.value,
    };

    setSubmitting(true);
    fetch(apiUrl + "/api/v1/enquiry/save", {
      method: "POST",
      headers,
      body: JSON.stringify(requestData),
    })
      .then((response) => {
        if (response.status === 201) {
          setStatus({
            type: "success",
            message: "Thanks! Your enquiry has been sent — our team will reach out shortly.",
          });
          autoCloseTimer.current = setTimeout(handleClose, 3500);
          return;
        }

        if (response.status === 401) {
          setStatus({ type: "error", message: "Session expired. Please login again." });
          return;
        }

        if (response.status === 403) {
          setStatus({ type: "error", message: "Access denied." });
          return;
        }

        setStatus({ type: "error", message: "Failed to send enquiry. Please try again." });
      })
      .catch((error) => {
        console.error(
          "There was a problem sending the form data:",
          error.message
        );
        setStatus({ type: "error", message: "Failed to send enquiry. Please try again." });
      })
      .finally(() => setSubmitting(false));
  };

  const isSuccess = status.type === "success";

  return (
    <AnimatePresence onExitComplete={onClose}>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-slate-900/55 backdrop-blur-sm p-3"
          role="dialog"
          aria-modal="true"
          aria-label="Enquiry form"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
      <motion.div
        className="relative w-full max-w-[520px] max-h-[90vh] overflow-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl"
        initial={{ opacity: 0, y: 8, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 8, scale: 0.95 }}
        transition={{ duration: 0.15 }}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-900"
        >
          <span className="text-xl leading-none">&times;</span>
        </button>

        {isSuccess ? (
          <div className="flex flex-col items-center px-2 py-6 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                className="h-7 w-7"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mt-4 text-lg font-bold text-slate-900">Enquiry sent!</h2>
            <p className="mt-1 text-sm text-slate-600">{status.message}</p>
            <button
              type="button"
              onClick={handleClose}
              className="mt-5 w-full rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-700"
            >
              Continue browsing
            </button>
          </div>
        ) : (
          <>
            <div className="mb-4 pr-8">
              <h2 className="text-lg font-bold text-slate-900">Enquiry Form</h2>
              <p className="mt-1 text-sm text-slate-600">
                Share your details and we’ll help you with admissions.
              </p>
            </div>

            {status.message && (
              <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
                {status.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label htmlFor="name" className="text-sm font-semibold text-slate-700">
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your name"
                  required
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="email" className="text-sm font-semibold text-slate-700">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  required
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="phone" className="text-sm font-semibold text-slate-700">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                  required
                  className={inputClass}
                />
              </div>

              <div>
                <label htmlFor="state" className="text-sm font-semibold text-slate-700">
                  State
                </label>
                <div className="mt-1">
                  <Select
                    inputId="state"
                    placeholder="Select State"
                    value={formData.state}
                    onChange={handleStateChange}
                    options={indianStates}
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="course" className="text-sm font-semibold text-slate-700">
                  Course
                </label>
                <div className="mt-1">
                  <Select
                    inputId="course"
                    placeholder="Select Course"
                    value={formData.course}
                    onChange={handleCourseChange}
                    options={courses}
                    styles={selectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  />
                </div>
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="popup-consent"
                  checked={formData.consent}
                  onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                  required
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-brand-600"
                />
                <label htmlFor="popup-consent" className="text-xs leading-snug text-slate-600 cursor-pointer">
                  I consent to receive SMS, email, and WhatsApp messages and agree to the{" "}
                  <a href="/terms" className="text-brand-600 hover:underline">terms and conditions</a>.
                </label>
              </div>

              <button
                type="submit"
                disabled={submitting || !formData.consent}
                className={
                  "w-full rounded-xl px-4 py-2 text-sm font-semibold text-white transition " +
                  (submitting || !formData.consent
                    ? "bg-slate-400 cursor-not-allowed"
                    : "bg-brand-600 hover:bg-brand-700")
                }
              >
                {submitting ? "Sending…" : "Send Enquiry"}
              </button>
            </form>
          </>
        )}
      </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EnquiryPopupForm;
